import { FIELD_NAMES } from 'modules/projectSettings/constants';
import { memo, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { match } from 'ts-pattern';
import { BUTTON_SIZES, SecondaryButton } from 'ui/button';
import { Add16 } from 'ui/icons';
import { ListEmptyTableState, NoResult } from 'ui/illustrations';
import {
  MODAL_NAMES,
  ModalState,
  ModalStatePayload,
  ProjectSettingsFormSchemaType,
  useProjectSettingsModalsContext,
} from '../../contexts';
import { ProjectLabel } from '../../types';
import { List, ListBody, ListFooter, ListHeader, StyledPlaceholderState } from '../List';
import { ListContextProvider, useListContext } from '../List/ListContext';
import { LabelListRow } from './LabelsListRow';
import { sortObjectListByKey } from 'utils/arrayHelpers';

const { LABELS } = FIELD_NAMES;

const AddButton = () => {
  const { showModal } = useProjectSettingsModalsContext();
  return (
    <SecondaryButton
      size={BUTTON_SIZES.LARGE}
      type="button"
      onClick={() => showModal({ name: MODAL_NAMES.ADD_NEW_LABEL })}
    >
      <Add16 />
      Add new label
    </SecondaryButton>
  );
};

const FilteredLabelsList = ({ filteredItems }: { filteredItems: ProjectLabel[] }) => {
  const { showModal } = useProjectSettingsModalsContext();

  return <MemoLabelsList filteredItems={filteredItems} showModal={showModal} />;
};

const MemoLabelsList = memo(
  ({
    filteredItems,
    showModal,
  }: {
    filteredItems: ProjectLabel[];
    showModal: (modalState: ModalState<ModalStatePayload>) => void;
  }) => {
    return filteredItems.map((label) => (
      <LabelListRow key={label.id} label={label} showModal={showModal} />
    ));
  },
);

const BaseLabelsList = () => {
  const { filteredItems, hasNoItems, hasNoMatchingItems } = useListContext<ProjectLabel>();

  return (
    <List>
      <ListHeader>
        <AddButton />
      </ListHeader>

      <ListBody data-empty={hasNoItems || hasNoMatchingItems}>
        {match({ hasNoItems, hasNoMatchingItems })
          .with({ hasNoItems: true }, () => (
            <StyledPlaceholderState title="No labels yet" Illustration={ListEmptyTableState}>
              Create your first label to get started.
            </StyledPlaceholderState>
          ))
          .with({ hasNoMatchingItems: true }, () => (
            <StyledPlaceholderState title="No matching results" Illustration={NoResult}>
              Try searching by a different keyword.
            </StyledPlaceholderState>
          ))
          .otherwise(() => (
            <FilteredLabelsList filteredItems={filteredItems} />
          ))}
      </ListBody>

      <ListFooter />
    </List>
  );
};

const filterLabels = (item: ProjectLabel, query: string) =>
  item?.name?.toLowerCase()?.includes(query?.toLowerCase());

export const LabelsList = () => {
  const { watch } = useFormContext<ProjectSettingsFormSchemaType>();

  const labels = watch(LABELS);
  const sortedLabels = useMemo(() => sortObjectListByKey(labels, 'name'), [labels]);

  return (
    <ListContextProvider items={sortedLabels} filterFn={filterLabels}>
      <BaseLabelsList />
    </ListContextProvider>
  );
};
